/**
 * Single source of truth for loan + investment schedule simulation (portal + mobile hook).
 */
import { INVESTMENT_RATE_MONTHLY } from "./loanConstants";

/**
 * @param {object} params
 * @param {number|string} params.totalDebt
 * @param {number|string} params.constrainedMonthlyPayment
 * @param {number} params.effectivePlanYears
 * @param {number} params.totalTermYears effectivePlanYears + delayYears
 * @param {boolean} params.enableDelay
 * @param {number} params.delayYears
 * @param {number} params.delayMonths
 * @param {boolean} params.enableGrowth
 * @param {number} params.gr payment growth decimal (e.g. growthRate/1000)
 * @param {number} params.igr investment contribution growth decimal
 * @param {boolean} params.enableInvestment
 * @param {number|string} params.monthlyInvestment
 * @param {number|string} params.startingInvestmentCapital
 * @param {Array<{amount?: number, year?: number}>} params.oneTimeRepayments
 * @param {(monthIndex: number) => number} params.getMonthlyRateForMonth
 */
export function computePortalSchedule({
  totalDebt,
  constrainedMonthlyPayment,
  effectivePlanYears,
  totalTermYears,
  enableDelay,
  delayYears,
  delayMonths,
  enableGrowth,
  gr,
  igr,
  enableInvestment,
  monthlyInvestment,
  startingInvestmentCapital,
  oneTimeRepayments,
  getMonthlyRateForMonth,
}) {
  let balance = Math.max(0, Number(totalDebt) || 0);
  const basePay = Math.max(0, Number(constrainedMonthlyPayment) || 0);
  const startingCapital = Number(startingInvestmentCapital) || 0;
  const delayMonthsTotal = enableDelay ? delayYears * 12 + delayMonths : 0;
  const rows = [{ m: 0, balance, interestCumulative: 0, investmentValue: startingCapital }];

  if (basePay <= 0 || balance <= 0) {
    return {
      schedule: rows,
      payoffMonths: 0,
      interestAtEnd: 0,
      finalInvestmentValue: startingCapital,
      payoffByInvestment: false,
    };
  }

  let m = 0;
  let cumInterest = 0;
  let investmentValue = startingCapital;
  const maxMonths =
    enableDelay && (delayYears > 0 || delayMonths > 0)
      ? totalTermYears * 12 + delayMonths
      : effectivePlanYears * 12;
  let lastBefore = { m: 0, balance, cumInterest, investmentValue };
  let payoffByInvestment = false;

  if (delayMonthsTotal > 0) {
    for (let dm = 0; dm < delayMonthsTotal; dm++) {
      m = dm;
      const monthlyInterest = balance * getMonthlyRateForMonth(dm);
      cumInterest += monthlyInterest;
      balance = balance + monthlyInterest;
      const oneTimeForMonth = (oneTimeRepayments || [])
        .filter((r) => r && (r.amount || 0) > 0)
        .filter((r) => {
          if (r.year === 0) return (m + 1) % 12 === 0;
          return (r.year || 0) >= 1 && (r.year || 0) * 12 === m + 1;
        })
        .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
      if (oneTimeForMonth > 0) {
        balance = Math.max(0, balance - oneTimeForMonth);
      }
      if (enableInvestment && investmentValue > 0) {
        investmentValue = investmentValue * (1 + INVESTMENT_RATE_MONTHLY);
      }
      rows.push({ m: m + 1, balance, interestCumulative: cumInterest, investmentValue });
      lastBefore = { m: m + 1, balance, cumInterest, investmentValue };
      if (balance <= 0) break;
    }
    if (balance > 0) {
      m = delayMonthsTotal;
    } else {
      m = lastBefore.m;
    }
  }

  while (balance > 0 && m < maxMonths) {
    let payment = basePay;
    if (enableGrowth) {
      const yearsElapsed = Math.floor(Math.max(0, (m - delayMonthsTotal) / 12));
      payment = basePay * Math.pow(1 + gr, yearsElapsed);
    }
    const P_t = balance;
    const interest_t = P_t * getMonthlyRateForMonth(m);
    const amountToPayOff = P_t + interest_t;
    let invest_contrib_t = 0;
    let loan_service_t = payment;
    let interestPaid;
    let principal_repay_t;
    let unpaid_interest_t = 0;
    const canPayOffWithPaymentAndInvest =
      enableInvestment && investmentValue + payment >= amountToPayOff && amountToPayOff > 0;
    if (canPayOffWithPaymentAndInvest) {
      loan_service_t = Math.min(payment, amountToPayOff);
      interestPaid = interest_t;
      principal_repay_t = loan_service_t - interest_t;
      invest_contrib_t = 0;
    } else {
      if (payment >= interest_t) {
        interestPaid = interest_t;
        principal_repay_t = payment - interest_t;
      } else {
        interestPaid = payment;
        principal_repay_t = 0;
        unpaid_interest_t = interest_t - payment;
      }
      const wouldBeNewBalance = P_t - principal_repay_t + unpaid_interest_t;
      if (enableInvestment && wouldBeNewBalance > 0) {
        let ic = monthlyInvestment;
        if (enableGrowth) {
          const yearsElapsed = Math.floor(Math.max(0, (m - delayMonthsTotal) / 12));
          ic = monthlyInvestment * Math.pow(1 + igr, yearsElapsed);
        }
        const invAfterGrowth = investmentValue * (1 + INVESTMENT_RATE_MONTHLY);
        if (invAfterGrowth + ic >= wouldBeNewBalance && wouldBeNewBalance > 0) {
          invest_contrib_t = Math.min(ic, Math.max(0, wouldBeNewBalance - invAfterGrowth));
        } else {
          invest_contrib_t = ic;
        }
      }
    }
    const investmentValueAfterGrowth = investmentValue * (1 + INVESTMENT_RATE_MONTHLY) + invest_contrib_t;
    const wouldBeNewBalance = P_t - principal_repay_t + unpaid_interest_t;
    const wouldBeFinalMonth =
      enableInvestment &&
      investmentValueAfterGrowth >= wouldBeNewBalance &&
      wouldBeNewBalance > 0;
    let newBalance;
    if (wouldBeFinalMonth && !canPayOffWithPaymentAndInvest) {
      payoffByInvestment = true;
      if (principal_repay_t > P_t) principal_repay_t = P_t;
      newBalance = 0;
    } else if (canPayOffWithPaymentAndInvest) {
      newBalance = 0;
    } else {
      newBalance = wouldBeNewBalance;
    }
    investmentValue = investmentValueAfterGrowth;
    if (enableInvestment && investmentValue > newBalance && newBalance > 0) {
      investmentValue = newBalance;
    }
    const oneTimeForMonth = (oneTimeRepayments || [])
      .filter((r) => r && (r.amount || 0) > 0)
      .filter((r) => {
        if (r.year === 0) return (m + 1) % 12 === 0;
        return (r.year || 0) >= 1 && (r.year || 0) * 12 === m + 1;
      })
      .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    if (oneTimeForMonth > 0) {
      newBalance = Math.max(0, newBalance - oneTimeForMonth);
      balance = newBalance;
    }
    cumInterest += interestPaid;
    rows.push({
      m: m + 1,
      balance: newBalance,
      interestCumulative: cumInterest,
      investmentValue,
      loanPayment: loan_service_t,
      investmentContribution: invest_contrib_t,
    });
    lastBefore = { m: m + 1, balance: newBalance, cumInterest, investmentValue };
    balance = newBalance;
    m += 1;
    if (newBalance <= 0 || wouldBeFinalMonth) break;
  }

  let payoffMonths = m;
  let interestAtEnd = cumInterest;
  let finalInvestmentValue = investmentValue;

  if (rows.length >= 2 && rows[rows.length - 1].balance === 0 && !payoffByInvestment) {
    const balanceStart = lastBefore.balance;
    const yearsElapsed = enableGrowth
      ? Math.floor((lastBefore.m - delayMonthsTotal - 1) / 12)
      : Math.floor((lastBefore.m - 1) / 12);
    let finalPay = basePay;
    if (enableGrowth) {
      finalPay = basePay * Math.pow(1 + gr, Math.max(0, yearsElapsed));
    }
    const frac = Math.min(1, balanceStart / Math.max(1e-9, finalPay));
    payoffMonths = lastBefore.m - 1 + frac;
    const prevCum = rows[rows.length - 2]?.interestCumulative || 0;
    const currCum = rows[rows.length - 1].interestCumulative;
    interestAtEnd = prevCum + (currCum - prevCum) * frac;

    if (enableInvestment && investmentValue > 0) {
      const investmentValueBefore = lastBefore.investmentValue || 0;
      finalInvestmentValue = investmentValueBefore * Math.pow(1 + INVESTMENT_RATE_MONTHLY, frac);
      let monthlyInvestmentContribution = monthlyInvestment;
      if (enableGrowth) {
        const ye = Math.floor((lastBefore.m - delayMonthsTotal - 1) / 12);
        monthlyInvestmentContribution = monthlyInvestment * Math.pow(1 + igr, Math.max(0, ye));
      }
      if (monthlyInvestmentContribution > 0) {
        finalInvestmentValue = finalInvestmentValue + monthlyInvestmentContribution * frac;
      }
    }
  }

  return {
    schedule: rows,
    payoffMonths,
    interestAtEnd,
    finalInvestmentValue,
    payoffByInvestment,
  };
}
